import type { KonvaEventObject } from 'konva/lib/Node';
import React, { useRef } from 'react';
import { Line } from 'react-konva';
import { useDragGesture } from 'src/hooks/useDragGesture';

type Point = {
    x: number;
    y: number;
};

interface Props {
    scaledPoints: Point[];
    points: Point[];
    selected: boolean;
    scale?: number;
    selectLayer: () => void;
    onDragMove: (points: Point[]) => void;
    onDragEnd: () => void;
}

interface DragState {
    clientX: number;
    clientY: number;
    startPoints: Point[];
    scale: number;
}

const Border: React.FC<Props> = ({ scaledPoints, points, selected, scale = 1, selectLayer, onDragMove, onDragEnd }) => {
    // Pure drag bookkeeping -- never read in render -- refreshed every render so `handleDragMove`
    // (registered for the whole gesture by `useDragGesture`) always sees the latest `scale`.
    const dragState = useRef<DragState>({ clientX: 0, clientY: 0, startPoints: [], scale });
    dragState.current.scale = scale;

    const handleDragMove = (e: MouseEvent) => {
        const { clientX, clientY, startPoints, scale: currentScale } = dragState.current;
        const deltaX = (clientX - e.clientX) / currentScale;
        const deltaY = (clientY - e.clientY) / currentScale;
        const newPoints = startPoints.map((point) => ({ x: point.x - deltaX, y: point.y - deltaY }));

        onDragMove(newPoints);
    };

    const { start } = useDragGesture({ onDragMove: handleDragMove, onDragEnd });

    const onDragStart = (e: KonvaEventObject<MouseEvent>) => {
        dragState.current.clientX = e.evt.clientX;
        dragState.current.clientY = e.evt.clientY;
        dragState.current.startPoints = points;

        start();
    };

    const onMouseDown = (e: KonvaEventObject<MouseEvent>) => {
        e.cancelBubble = true;
        if (selected) {
            onDragStart(e);
        } else {
            selectLayer();
        }
    };

    const reversePoints = (pointsArray: number[]) => {
        const reverse: number[] = [];

        for (let i = pointsArray.length - 1; i >= 0; i -= 2) {
            reverse.push(pointsArray[i - 1]);
            reverse.push(pointsArray[i]);
        }

        return reverse;
    };

    const renderLine = (segment: number[], color: string, closed: boolean) => {
        return (
            <Line
                key={segment.join(',')}
                points={segment}
                stroke={color}
                strokeWidth={1}
                closed={closed}
                onMouseDown={onMouseDown}
            />
        );
    };

    const pointArray = scaledPoints.reduce((acc, curr) => acc.concat([curr.x, curr.y]), [] as number[]);

    const color = selected ? 'red' : 'transparent';

    if (pointArray.length > 8) {
        const squareSize = Math.sqrt(pointArray.length / 2) * 2;

        // map out the horizontal lines and set horizontal border lines
        const horizontalLines: number[][] = [];

        for (let i = 0; i < pointArray.length; i += squareSize) {
            horizontalLines.push(pointArray.slice(i, i + squareSize));
        }

        // map out vertical lines and set vertical border lines
        const verticalLines: number[][] = [];
        for (let col = 0; col < squareSize; col += 2) {
            const line: number[] = [];

            for (let row = 0; row < squareSize / 2; row++) {
                line.push(horizontalLines[row][col]);
                line.push(horizontalLines[row][col + 1]);
            }

            verticalLines.push(line);
        }

        // render border lines as one line
        const border = [
            ...horizontalLines[0],
            ...verticalLines[verticalLines.length - 1],
            ...reversePoints(horizontalLines[horizontalLines.length - 1]),
            ...reversePoints(verticalLines[0]),
        ];

        const lineSegment: Array<JSX.Element | JSX.Element[]> = [];
        lineSegment.push(horizontalLines.map((segment) => renderLine(segment, color, false)));
        lineSegment.push(verticalLines.map((segment) => renderLine(segment, color, false)));
        lineSegment.push(renderLine(border, color, true));

        return lineSegment;
    }

    return renderLine(pointArray, color, true);
};

export default Border;
